import {
  ITEM_TYPES_WITH_OPTIONS,
  normalizeChecklistItemOptions,
  parseOptionsFromCSV,
} from './checklistItemUtils';

export interface CSVItem {
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  options: string[];
}

export interface CSVValidationError {
  row: number;
  column: string;
  message: string;
  value: string;
}

export interface CSVValidationResult {
  items: CSVItem[];
  errors: CSVValidationError[];
  isValid: boolean;
}

// User-friendly item type mapping
const ITEM_TYPE_MAPPING: { [key: string]: string } = {
  'text': 'text_input',
  'text input': 'text_input',
  'Text': 'text_input',
  'Text Input': 'text_input',
  'number': 'number_input',
  'number input': 'number_input',
  'Number': 'number_input',
  'Number Input': 'number_input',
  'boolean': 'checkbox',
  'yes/no': 'checkbox',
  'Yes/No': 'checkbox',
  'checkbox': 'checkbox',
  'Checkbox': 'checkbox',
  'file': 'file_upload',
  'file upload': 'file_upload',
  'File': 'file_upload',
  'File Upload': 'file_upload',
  'date': 'date_input',
  'date input': 'date_input',
  'Date': 'date_input',
  'Date Input': 'date_input',
  'select': 'select',
  'dropdown': 'select',
  'Dropdown': 'select',
  'Select': 'select',
  'radio': 'radio',
  'Radio': 'radio',
  'Radio Buttons': 'radio',
  'multi_select': 'multi_select',
  'multi-select': 'multi_select',
  'Multi-select': 'multi_select',
  'Multi Select': 'multi_select'
};

// Helper function to parse a CSV line respecting quoted fields
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add last field
  result.push(current.trim());
  
  return result;
}

export function parseCSV(csvText: string): CSVValidationResult {
  const lines = csvText.trim().split('\n');
  const errors: CSVValidationError[] = [];
  const items: CSVItem[] = [];

  // Check if we have at least a header row
  if (lines.length < 2) {
    errors.push({
      row: 0,
      column: 'general',
      message: 'CSV must have at least a header row and one data row',
      value: ''
    });
    return { items, errors, isValid: false };
  }

  // Parse header row using proper CSV parsing
  const headerRow = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  const expectedHeaders = ['title', 'description', 'item_type', 'order', 'is_required'];
  
  // Validate headers
  const missingHeaders = expectedHeaders.filter(h => !headerRow.includes(h));
  if (missingHeaders.length > 0) {
    errors.push({
      row: 0,
      column: 'headers',
      message: `Missing required columns: ${missingHeaders.join(', ')}`,
      value: headerRow.join(', ')
    });
  }

  const colIndex = (name: string) => headerRow.indexOf(name);
  const titleIdx = colIndex('title');
  const descriptionIdx = colIndex('description');
  const itemTypeIdx = colIndex('item_type');
  const orderIdx = colIndex('order');
  const isRequiredIdx = colIndex('is_required');
  const optionsIdx = colIndex('options');

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    const values = parseCSVLine(line); // Use proper CSV parsing
    
    // Ensure we have enough columns for required fields
    if (values.length < expectedHeaders.length) {
      errors.push({
        row: i + 1,
        column: 'general',
        message: `Row has ${values.length} columns, expected at least ${expectedHeaders.length}`,
        value: line
      });
      continue;
    }

    const rawItemType = values[itemTypeIdx] || '';
    const normalizedType = normalizeItemType(rawItemType);
    
    const item: CSVItem = {
      title: values[titleIdx] || '',
      description: descriptionIdx >= 0 ? (values[descriptionIdx] || '') : '',
      item_type: normalizedType,
      order: parseInt(values[orderIdx]) || 0,
      is_required: parseBoolean(values[isRequiredIdx] || ''),
      options: optionsIdx >= 0 ? parseOptionsFromCSV(values[optionsIdx] || '') : [],
    };

    // Validate the item
    const itemErrors = validateCSVItem(item, i + 1);
    errors.push(...itemErrors);

    if (itemErrors.length === 0) {
      items.push(item);
    }
  }

  return {
    items,
    errors,
    isValid: errors.length === 0
  };
}

function validateCSVItem(item: CSVItem, rowNumber: number): CSVValidationError[] {
  const errors: CSVValidationError[] = [];

  // Validate title
  if (!item.title.trim()) {
    errors.push({
      row: rowNumber,
      column: 'title',
      message: 'Title is required',
      value: item.title
    });
  }

  // Validate item_type using the normalization function
  const normalizedItemType = normalizeItemType(item.item_type);
  const validBackendTypes = [
    'text_input',
    'number_input',
    'checkbox',
    'file_upload',
    'date_input',
    'select',
    'multi_select',
    'radio',
  ];
  
  if (!validBackendTypes.includes(normalizedItemType)) {
    // Get user-friendly examples of valid types (showing common variations)
    const examples = [
      'text/Text/Text Input',
      'number/Number/Number Input',
      'checkbox/Checkbox',
      'file/File/File Upload',
      'date/Date/Date Input',
      'dropdown/select/Select',
      'multi-select/Multi-select/Multi Select'
    ];
    errors.push({
      row: rowNumber,
      column: 'item_type',
      message: `Invalid item type "${item.item_type}". Must be one of: ${examples.join(', ')}`,
      value: item.item_type
    });
  }

  const normalizedOptions = normalizeChecklistItemOptions(item.options);
  if (
    ITEM_TYPES_WITH_OPTIONS.includes(normalizedItemType as typeof ITEM_TYPES_WITH_OPTIONS[number]) &&
    normalizedOptions.length === 0
  ) {
    errors.push({
      row: rowNumber,
      column: 'options',
      message:
        'Dropdown, radio, and multi-select items require options (semicolon-separated in the options column)',
      value: '',
    });
  }

  // Validate order
  if (isNaN(item.order) || item.order < 0) {
    errors.push({
      row: rowNumber,
      column: 'order',
      message: 'Order must be a positive number',
      value: item.order.toString()
    });
  }

  return errors;
}

function parseBoolean(value: string): boolean {
  const normalized = value.toLowerCase().trim();
  return normalized === 'true';
}

export function normalizeItemType(itemType: string): string {
  // Trim whitespace and convert to lowercase
  const cleaned = itemType.trim().toLowerCase();
  
  // Try direct lookup first
  if (ITEM_TYPE_MAPPING[cleaned]) {
    return ITEM_TYPE_MAPPING[cleaned];
  }
  
  // Try case-insensitive lookup
  const entries = Object.entries(ITEM_TYPE_MAPPING);
  for (const [key, value] of entries) {
    if (key.toLowerCase() === cleaned) {
      return value;
    }
  }
  
  // If no match found, return the original (will fail validation)
  return itemType;
}

export function generateCSVTemplate(): string {
  const headers = ['title', 'description', 'item_type', 'order', 'is_required', 'options'];
  const examples = [
    ['Safety Check', 'Verify all safety equipment is in place, mounted, and clearly visible', 'checkbox', '1', 'TRUE', ''],
    ['Temperature Reading', 'Record the current temperature, in Fahrenheit or Celsius', 'Number', '2', 'TRUE', ''],
    ['Equipment Photo', 'Take a photo of the equipment, showing all sides', 'File Upload', '3', 'FALSE', ''],
    ['Inspection Date', 'Date of inspection, must be current', 'Date', '4', 'TRUE', ''],
    ['Equipment Type', 'Select the type of equipment', 'Dropdown', '5', 'TRUE', 'Type A;Type B;Type C'],
    ['Hazards Observed', 'Select all hazards that apply', 'Multi-select', '6', 'FALSE', 'Slip;Trip;Electrical;Chemical'],
  ];

  // Helper to escape and quote fields that contain commas
  const escapeCSVField = (field: string): string => {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  const csvContent = [
    headers.join(','),
    ...examples.map(row => row.map(escapeCSVField).join(','))
  ].join('\n');

  return csvContent;
}

export function downloadCSVTemplate(): void {
  const csvContent = generateCSVTemplate();
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'checklist_items_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
} 